/* eslint-disable no-param-reassign */
import { setCookieCSR } from '@/app/api/CSR/setCookieCSR'
import { useCallback, useEffect, useMemo, useState } from 'react'
import buildCustomBreakpoints from './adapter/buildCustomBreakpoints'

const minWidthEm = (value: number) => `(min-width: ${value}em)`

const matchBy = (values: string) =>
  typeof window !== 'undefined'
    ? window.matchMedia(values)
    : {
        matches: false,
        onchange: null,
      }

const useBreakpointCode = (props: {
  initialCode?: string
  customBreakpoints?: { [key: string]: string | number }
}): [string, boolean] => {
  const { initialCode, customBreakpoints } = props || {}

  // Memoize points to prevent recreation on every render
  const points: { [key: string]: { em: number; name: string } } = useMemo(
    () => buildCustomBreakpoints(customBreakpoints || {}),
    [customBreakpoints]
  )

  const [breakpointCode, setBreakpointCode] = useState(initialCode || '')
  const [checkTrigger, setCheckTrigger] = useState(false)

  // Memoize compare function to prevent recreation on every render
  const compare = useCallback(
    (oldBp: string, currentBp: string) => {
      const a = points[oldBp]
      const b = points[currentBp]

      if (a.em < b.em) {
        return -1
      }
      if (a.em > b.em) {
        return 1
      }
      return 0
    },
    [points]
  )

  // Memoize mediaPointsKeys to prevent recalculation on every render
  const mediaPointsKeys = useMemo(
    () => Object.keys(points).sort(compare),
    [points, compare]
  )

  // Set cookie only when breakpointCode changes
  useEffect(() => {
    setCookieCSR('_IL-breakpointCode', breakpointCode)
  }, [breakpointCode])

  useEffect(() => {
    const mediaPoints = mediaPointsKeys.map((key) => {
      const point = points[key] as { em: number; name: string }
      if (!point) {
        throw new Error(`Breakpoint ${key} not found`)
      }
      if (!point?.em) {
        throw new Error('em value is required')
      }
      return {
        matchName: point.name,
        matchMedia: matchBy(minWidthEm(point.em)),
      }
    })

    const check = () => {
      let matched = mediaPoints[0].matchName || ''
      mediaPoints.forEach((mediaPoint) => {
        if (mediaPoint.matchMedia.matches) {
          matched = mediaPoint.matchName
        }
      })
      setBreakpointCode(matched)
      setCheckTrigger(true)
    }

    // Initial check
    check()

    // Add event listeners
    mediaPoints.forEach((mediaPoint) => {
      mediaPoint.matchMedia.onchange = check
    })

    // Cleanup function to remove event listeners
    return () => {
      mediaPoints.forEach((mediaPoint) => {
        if (mediaPoint.matchMedia && mediaPoint.matchMedia.onchange) {
          mediaPoint.matchMedia.onchange = null
        }
      })
    }
  }, [mediaPointsKeys, points])

  return [breakpointCode, checkTrigger]
}

export default useBreakpointCode
